import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  FileText, CheckCircle, Lock, ChevronRight, 
  Edit3, Type, Calendar, Mail, AlertCircle,
  Download, ArrowLeft, ArrowRight, Loader2, Shield
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Signature Pad Component
const SignaturePad = ({ onSignature, signatureType, setSignatureType }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    if (signatureType === 'drawn' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0D0D0D';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#D4AF37';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [signatureType]);

  const startDrawing = (e) => {
    if (signatureType !== 'drawn') return;
    setIsDrawing(true);
    setHasDrawn(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || signatureType !== 'drawn') return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (signatureType === 'drawn' && canvasRef.current && hasDrawn) {
      onSignature(canvasRef.current.toDataURL());
    }
  };

  const clearCanvas = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0D0D0D';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setHasDrawn(false);
      onSignature('');
    }
  };

  const handleTypedChange = (e) => {
    const name = e.target.value;
    setTypedName(name);
    onSignature(name);
  };

  return (
    <div className="space-y-4">
      {/* Signature Type Toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setSignatureType('typed')}
          className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
            signatureType === 'typed'
              ? 'bg-[#D4AF37] text-black'
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
        >
          <Type className="w-4 h-4 inline mr-2" />
          Type Signature
        </button>
        <button
          onClick={() => setSignatureType('drawn')}
          className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
            signatureType === 'drawn'
              ? 'bg-[#D4AF37] text-black'
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
        >
          <Edit3 className="w-4 h-4 inline mr-2" />
          Draw Signature
        </button>
      </div>

      {/* Signature Input */}
      {signatureType === 'typed' ? (
        <div>
          <input
            type="text"
            value={typedName}
            onChange={handleTypedChange}
            placeholder="Type your full legal name"
            className="w-full px-4 py-3 bg-[#0D0D0D] border border-white/20 text-white text-xl font-signature focus:border-[#D4AF37] focus:outline-none"
            style={{ fontFamily: "'Dancing Script', cursive" }}
            data-testid="typed-signature-input"
          />
          <p className="text-white/40 text-xs mt-1">Your typed name serves as your legal signature</p>
        </div>
      ) : (
        <div>
          <div className="relative border border-white/20 bg-[#0D0D0D]">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              className="w-full cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              data-testid="signature-canvas"
            />
            <button
              onClick={clearCanvas}
              className="absolute top-2 right-2 px-2 py-1 bg-white/10 text-white/60 text-xs hover:bg-white/20"
            >
              Clear
            </button>
          </div>
          <p className="text-white/40 text-xs mt-1">Draw your signature above</p>
        </div>
      )}
    </div>
  );
};

// Document Content Renderer
const DocumentContent = ({ content, fieldValues, setFieldValues, readOnly = false }) => {
  // Parse markdown-like content and replace field placeholders with inputs
  const renderContent = () => {
    // Split content into lines
    const lines = content.split('\n');
    
    return lines.map((line, index) => {
      // Headers
      if (line.startsWith('# ')) {
        return <h1 key={index} className="text-2xl font-bold text-[#D4AF37] mb-4 mt-6">{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-bold text-white mb-3 mt-4">{line.slice(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className="text-lg font-bold text-white mb-2 mt-3">{line.slice(4)}</h3>;
      }
      
      // Bold text
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
      
      // List items
      if (line.startsWith('- ')) {
        return (
          <li key={index} className="text-white/70 ml-4 mb-1" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />
        );
      }
      
      // Checkbox items
      if (line.startsWith('☑ ')) {
        return (
          <div key={index} className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-white/70" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />
          </div>
        );
      }
      
      // Tables
      if (line.startsWith('|')) {
        const cells = line.split('|').filter(Boolean).map(c => c.trim());
        if (cells.every(c => c.match(/^[-:]+$/))) {
          return null; // Skip separator rows
        }
        return (
          <tr key={index} className="border-b border-white/10">
            {cells.map((cell, i) => {
              // Check if cell contains a field placeholder
              const fieldMatch = cell.match(/\$___(\w+)___/);
              if (fieldMatch && !readOnly) {
                const fieldName = fieldMatch[1];
                return (
                  <td key={i} className="py-2 px-3">
                    <input
                      type="text"
                      value={fieldValues[fieldName] || ''}
                      onChange={(e) => setFieldValues({ ...fieldValues, [fieldName]: e.target.value })}
                      className="w-full px-2 py-1 bg-[#1a1a1a] border border-white/20 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
                      placeholder={fieldName.replace(/_/g, ' ')}
                    />
                  </td>
                );
              }
              return (
                <td key={i} className="py-2 px-3 text-white/70" dangerouslySetInnerHTML={{ __html: cell }} />
              );
            })}
          </tr>
        );
      }
      
      // Regular paragraphs with field placeholders
      if (line.includes('$___') && !readOnly) {
        const parts = line.split(/(\$___\w+___)/);
        return (
          <p key={index} className="text-white/70 mb-2 flex flex-wrap items-center gap-1">
            {parts.map((part, i) => {
              const fieldMatch = part.match(/\$___(\w+)___/);
              if (fieldMatch) {
                const fieldName = fieldMatch[1];
                if (fieldName === 'signature') {
                  return <span key={i} className="text-[#D4AF37]">[Signature Below]</span>;
                }
                if (fieldName === 'date') {
                  return <span key={i} className="text-[#D4AF37]">[Auto-filled]</span>;
                }
                return (
                  <input
                    key={i}
                    type="text"
                    value={fieldValues[fieldName] || ''}
                    onChange={(e) => setFieldValues({ ...fieldValues, [fieldName]: e.target.value })}
                    className="px-2 py-1 bg-[#1a1a1a] border border-white/20 text-white text-sm focus:border-[#D4AF37] focus:outline-none min-w-[120px]"
                    placeholder={fieldName.replace(/_/g, ' ')}
                  />
                );
              }
              return <span key={i} dangerouslySetInnerHTML={{ __html: part }} />;
            })}
          </p>
        );
      }
      
      // Empty lines
      if (!line.trim()) {
        return <div key={index} className="h-2" />;
      }
      
      // Regular paragraph
      return (
        <p key={index} className="text-white/70 mb-2" dangerouslySetInnerHTML={{ __html: line }} />
      );
    });
  };

  // Check if content has table rows
  const hasTable = content.includes('| Field |') || content.includes('| Item |') || content.includes('| Service |') || content.includes('| Fee |') || content.includes('| Tier |') || content.includes('| Charge |');
  
  return (
    <div className="prose prose-invert max-w-none">
      {hasTable ? (
        <table className="w-full border-collapse border border-white/10 mb-4">
          <tbody>
            {renderContent()}
          </tbody>
        </table>
      ) : (
        renderContent()
      )}
    </div>
  );
};

// Main Document Signing Modal
const DocumentSigningModal = ({ document, userId, userEmail, onSign, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [docData, setDocData] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [signature, setSignature] = useState('');
  const [signatureType, setSignatureType] = useState('typed');
  const [printedName, setPrintedName] = useState('');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await axios.get(`${API}/documents/user/${userId}/document/${document.id}`);
        setDocData(response.data);
        setFieldValues(response.data.default_values || {});
        setPrintedName(response.data.default_values?.printed_name || '');
      } catch (err) {
        setError('Failed to load document');
      } finally {
        setLoading(false);
      }
    };
    fetchDocument();
  }, [document.id, userId]);

  const handleSign = async () => {
    if (!signature) {
      setError('Please provide your signature');
      return;
    }
    if (!printedName) {
      setError('Please enter your printed name');
      return;
    }

    setSigning(true);
    setError('');

    try {
      const response = await axios.post(`${API}/documents/sign`, {
        user_id: userId,
        document_id: document.id,
        signature_data: signature,
        signature_type: signatureType,
        printed_name: printedName,
        field_values: fieldValues
      });

      if (response.data.success) {
        onSign(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Documents
            </button>
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Shield className="w-5 h-5" />
              <span className="text-sm">Secure E-Sign</span>
            </div>
          </div>

          {/* Document */}
          <div className="bg-[#0D0D0D] border border-white/10">
            {/* Document Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">{docData?.title}</h1>
                  <p className="text-white/50 text-sm capitalize">{docData?.category} Document</p>
                </div>
              </div>
            </div>

            {/* Document Content */}
            <div className="p-6 max-h-[50vh] overflow-y-auto bg-[#0A0A0A] border-b border-white/10">
              <DocumentContent 
                content={docData?.content || ''} 
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
              />
            </div>

            {/* Signature Section */}
            <div className="p-6 space-y-6">
              <h3 className="text-lg font-bold text-white">Sign Document</h3>
              
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Signature Pad */}
              <SignaturePad 
                onSignature={setSignature}
                signatureType={signatureType}
                setSignatureType={setSignatureType}
              />

              {/* Printed Name */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Printed Name *</label>
                <input
                  type="text"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0D0D0D] border border-white/20 text-white focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Enter your full legal name"
                  data-testid="printed-name-input"
                />
              </div>

              {/* Auto-filled Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Date (Auto-filled)
                  </label>
                  <div className="px-4 py-3 bg-[#1a1a1a] border border-white/10 text-white/70">
                    {currentDate}
                  </div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email (Auto-filled)
                  </label>
                  <div className="px-4 py-3 bg-[#1a1a1a] border border-white/10 text-white/70 truncate">
                    {userEmail}
                  </div>
                </div>
              </div>

              {/* Agreement Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5 accent-[#D4AF37]"
                  required
                  data-testid="agreement-checkbox"
                />
                <span className="text-white/70 text-sm">
                  I agree that my electronic signature is the legal equivalent of my manual signature on this document. 
                  By signing, I acknowledge that I have read and agree to all terms.
                </span>
              </label>

              {/* Sign Button */}
              <button
                onClick={handleSign}
                disabled={signing || !signature || !printedName}
                className="w-full py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="sign-document-btn"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <Edit3 className="w-5 h-5" />
                    Sign Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Progress Tracker Component
const DocumentProgressTracker = ({ documents, currentIndex }) => {
  const signedCount = documents.filter(d => d.is_signed).length;
  const totalCount = documents.length;
  const percentage = Math.round((signedCount / totalCount) * 100);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/70 text-sm">Document Completion</span>
        <span className="text-[#D4AF37] font-bold">{signedCount}/{totalCount} Complete</span>
      </div>
      <div className="h-2 bg-white/10 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D47B] transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex mt-4 gap-1">
        {documents.map((doc, index) => (
          <div 
            key={doc.id}
            className={`flex-1 h-1 ${
              doc.is_signed 
                ? 'bg-[#D4AF37]' 
                : index === currentIndex 
                  ? 'bg-[#D4AF37]/50' 
                  : 'bg-white/10'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Main Document Signing Flow Component
const DocumentSigningFlow = ({ userId, userEmail, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [services, setServices] = useState([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [signingDocument, setSigningDocument] = useState(null);
  const [allSigned, setAllSigned] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents/user/${userId}/required`);
      setDocuments(response.data.documents);
      setServices(response.data.services);
      setAllSigned(response.data.completion_percentage === 100);
      
      // Find first unsigned document
      const firstUnsigned = response.data.documents.findIndex(d => !d.is_signed);
      if (firstUnsigned !== -1) {
        setCurrentDocIndex(firstUnsigned);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSign = (result) => {
    setSigningDocument(null);
    
    // Update local state
    setDocuments(prev => prev.map(d => 
      d.id === result.document_id ? { ...d, is_signed: true } : d
    ));
    
    if (result.all_documents_signed) {
      setAllSigned(true);
      onComplete?.();
    } else {
      // Move to next unsigned document
      const nextUnsigned = documents.findIndex((d, i) => i > currentDocIndex && !d.is_signed);
      if (nextUnsigned !== -1) {
        setCurrentDocIndex(nextUnsigned);
      } else {
        fetchDocuments();
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading your documents...</p>
        </div>
      </div>
    );
  }

  if (allSigned) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4" data-testid="onboarding-complete-screen">
        <div className="max-w-lg w-full">
          <div className="bg-gradient-to-br from-[#121212] to-[#0D0D0D] border-2 border-green-500/40 p-8 md:p-10 text-center relative overflow-hidden">
            {/* Decorative gold accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>

            {/* Success Icon */}
            <div className="w-24 h-24 bg-green-500/10 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6 relative">
              <CheckCircle className="w-12 h-12 text-green-500" />
              <div className="absolute inset-0 rounded-full bg-green-500/20 animate-ping"></div>
            </div>

            {/* Heading */}
            <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-black text-white uppercase tracking-wide mb-3">
              Onboarding Complete
            </h1>
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-semibold mb-6">
              Welcome to the Hive
            </p>

            {/* Summary */}
            <div className="bg-black/40 border border-white/5 p-4 mb-6">
              <p className="text-white/80 leading-relaxed">
                All onboarding documents have been <strong className="text-green-400">signed and completed.</strong>
              </p>
              <p className="text-white/50 text-sm mt-2">
                Your signed agreements are now safely stored in your{' '}
                <span className="text-[#D4AF37] font-semibold">Document Vault</span>{' '}
                and have been shared with the Queen B team.
              </p>
            </div>

            {/* What's Next */}
            <div className="text-left bg-white/5 p-4 mb-6 border-l-2 border-[#D4AF37]">
              <p className="text-white font-bold mb-2 text-sm uppercase tracking-wider">What's Next</p>
              <ul className="space-y-2 text-white/70 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Dashboard access is now <strong className="text-white">unlocked</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Signed agreements available under <strong className="text-white">Documents → Signed Agreements</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Your dispatcher will reach out within <strong className="text-white">24 hours</strong></span>
                </li>
              </ul>
            </div>

            {/* CTA */}
            <button
              onClick={onComplete}
              className="w-full px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2"
              data-testid="access-dashboard-btn"
            >
              Enter the Hive
              <ArrowRight className="w-5 h-5" />
            </button>

            <p className="text-white/30 text-xs mt-4">
              Queen B Logistics • Premium Freight Operations
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentDoc = documents[currentDocIndex];
  const categoryLabels = {
    global: 'Required for All',
    build: 'Build Services',
    protect: 'Protect Services',
    grow: 'Grow Services'
  };

  // Group documents by category
  const groupedDocs = documents.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-[#D4AF37]" />
            <h1 className="text-2xl font-bold text-white">Document Signing Center</h1>
          </div>
          <p className="text-white/60">
            Please review and sign the following documents to activate your account
          </p>
        </div>

        {/* Progress Tracker */}
        <DocumentProgressTracker documents={documents} currentIndex={currentDocIndex} />

        {/* Document List */}
        <div className="space-y-6">
          {Object.entries(groupedDocs).map(([category, docs]) => (
            <div key={category}>
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37]" />
                {categoryLabels[category] || category}
              </h2>
              <div className="space-y-2">
                {docs.map((doc, docIndex) => {
                  const globalIndex = documents.findIndex(d => d.id === doc.id);
                  const isCurrent = globalIndex === currentDocIndex;
                  const isLocked = !doc.is_signed && globalIndex > currentDocIndex;
                  
                  return (
                    <button
                      key={doc.id}
                      onClick={() => !doc.is_signed && !isLocked && setSigningDocument(doc)}
                      disabled={doc.is_signed || isLocked}
                      className={`w-full p-4 flex items-center justify-between transition-all ${
                        doc.is_signed
                          ? 'bg-green-500/10 border border-green-500/30'
                          : isCurrent
                            ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/50 hover:bg-[#D4AF37]/20'
                            : isLocked
                              ? 'bg-white/5 border border-white/10 opacity-50 cursor-not-allowed'
                              : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`document-${doc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 flex items-center justify-center ${
                          doc.is_signed
                            ? 'bg-green-500/20'
                            : isCurrent
                              ? 'bg-[#D4AF37]/20'
                              : 'bg-white/10'
                        }`}>
                          {doc.is_signed ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : isLocked ? (
                            <Lock className="w-5 h-5 text-white/30" />
                          ) : (
                            <FileText className={`w-5 h-5 ${isCurrent ? 'text-[#D4AF37]' : 'text-white/50'}`} />
                          )}
                        </div>
                        <div className="text-left">
                          <p className={`font-medium ${
                            doc.is_signed ? 'text-green-400' : isCurrent ? 'text-white' : 'text-white/70'
                          }`}>
                            {doc.title}
                          </p>
                          <p className="text-white/40 text-sm">
                            {doc.is_signed ? 'Signed' : isLocked ? 'Complete previous documents first' : 'Ready to sign'}
                          </p>
                        </div>
                      </div>
                      {!doc.is_signed && !isLocked && (
                        <ChevronRight className={`w-5 h-5 ${isCurrent ? 'text-[#D4AF37]' : 'text-white/30'}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Continue Button */}
        {currentDoc && !currentDoc.is_signed && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setSigningDocument(currentDoc)}
              className="px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center gap-2 mx-auto"
              data-testid="continue-signing-btn"
            >
              <Edit3 className="w-5 h-5" />
              Continue Signing: {currentDoc.title}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Signing Modal */}
      {signingDocument && (
        <DocumentSigningModal
          document={signingDocument}
          userId={userId}
          userEmail={userEmail}
          onSign={handleSign}
          onClose={() => setSigningDocument(null)}
        />
      )}
    </div>
  );
};

export default DocumentSigningFlow;
export { DocumentSigningModal, DocumentProgressTracker, SignaturePad };
